import React, { useState, useEffect } from "react";
import "./ManageUser.css";
import emailjs from "emailjs-com";
import {
  Input,
  Table,
  Modal,
  Form,
  Select,
  Button,
  Avatar,
  message,
  Space,
  Popconfirm,
  Descriptions,
  Spin,
  Tag,
  Tooltip,
  Statistic,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { db, auth } from "../Firebase/FirebaseConnection.js";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { onSnapshot, runTransaction, Timestamp } from "firebase/firestore";

import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";

export const generateAvatarSeed = (identifier) => {
  if (!identifier || typeof identifier !== "string") {
    return "default-avatar";
  }

  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash.toString();
};

const ManageUser = () => {
  const { Search } = Input;
  const [issueBookModalVisible, setIssueBookModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [booksList, setBooksList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [bookSearchTerm, setBookSearchTerm] = useState("");
  const [filteredStaffList, setFilteredStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [borrowedBooksModalVisible, setBorrowedBooksModalVisible] =
    useState(false);
  const [borrowedBooks, setBorrowedBooks] = useState([]);
  const [yearLevels, setYearLevels] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [genders, setGenders] = useState([]);
  const [numCopies, setNumCopies] = useState(1);
  const [selectedBook, setSelectedBook] = useState(null);
  const [totalStaffCount, setTotalStaffCount] = useState(0);
  const [userIssueStatus, setUserIssueStatus] = useState({});
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  emailjs.init("9_gz3AUhp7IdotPd2");

  const sendEmail = async (issueDetails) => {
    try {
      const response = await emailjs.send(
        "service_lwcz77w",
        "template_cvivqrb",
        {
          to_name: issueDetails.borrowedBy,
          book_title: issueDetails.title,
          copies_borrowed: issueDetails.copiesBorrowed,
          book_author: issueDetails.author,
          due_date: issueDetails.dueDate.toDate().toLocaleDateString(),
          email: issueDetails.email,
        },
        "9_gz3AUhp7IdotPd2"
      );
    } catch (error) {}
  };

  const sendEmailBorrower = async (issueDetails) => {
    try {
      const response = await emailjs.send(
        "service_lwcz77w",
        "template_yrcnssr",
        {
          to_name: issueDetails.borrowedBy,
          book_title: issueDetails.title,
          copies_borrowed: issueDetails.copiesBorrowed,
          book_author: issueDetails.author,
          due_date: issueDetails.dueDate.toDate().toLocaleDateString(),
          email: issueDetails.email,
        },
        "9_gz3AUhp7IdotPd2"
      );
    } catch (error) {}
  };

  useEffect(() => {
    fetchStaffData();
    const unsubscribeBooks = listenToBooksData();
    checkUserIssues();

    return () => unsubscribeBooks();
  }, []);

  const showModal = () => {
    setEditingStaff(null);
    form.setFieldsValue({
      name: "",
      email: "",
      number: "",
      staffId: "",
      yearLevel: undefined,
      designation: undefined,
      status: undefined,
      gender: undefined,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setViewStaff(null);
    setEditingStaff(null);
  };

  const handleIssueBookModalCancel = () => {
    setIssueBookModalVisible(false);
    form.resetFields();
    setBookSearchTerm("");
    setSelectedStaff(null);
    setNumCopies(1);
    setSelectedBook(null);
  };

  const handleBookSelection = async (bookId) => {
    const book = booksList.find((b) => b.key === bookId);
    setSelectedBook(book);

    if (!selectedStaff) {
      message.error("Please select a staff member first!");
      return;
    }

    if (numCopies <= 0) {
      message.error(
        "Oops! The number of copies must be at least 1. Please adjust your input."
      );
      return;
    }

    try {
      const issuedQuerySnapshot = await getDocs(collection(db, "issuedBooks"));
      const issuedBook = issuedQuerySnapshot.docs.find((docSnapshot) => {
        const data = docSnapshot.data();
        return (
          data.bookId === bookId &&
          data.staffId === selectedStaff.key &&
          data.status !== "Resolved" &&
          data.status !== "Paid"
        );
      });

      if (issuedBook) {
        const returnedQuerySnapshot = await getDocs(
          collection(db, "returnedBooks")
        );
        const isReturned = returnedQuerySnapshot.docs.some((docSnapshot) => {
          const data = docSnapshot.data();
          return data.bookId === bookId && data.staffId === selectedStaff.key;
        });

        if (!isReturned) {
          Modal.confirm({
            title: "Duplicate Book Issue",
            content: (
              <div>
                <p>
                  The user <strong>{selectedStaff?.name}</strong> has already
                  borrowed the book <strong>"{book?.title}"</strong> and has not
                  returned it yet.
                </p>
                <p>Are you sure you want to issue this book again?</p>
              </div>
            ),
            okText: "Yes",
            cancelText: "No",
            onOk: () => handleIssueBook(bookId),
          });
          return;
        }
      }

      Modal.confirm({
        title: "Confirm Book Issue",
        content: (
          <div>
            Are you sure you want to issue <strong>{numCopies}</strong> copies
            of <strong>"{book?.title}"</strong> by{" "}
            <strong>{book?.author}</strong> to{" "}
            <strong>{selectedStaff?.name}</strong>?
          </div>
        ),
        okText: "Yes",
        cancelText: "No",
        onOk: () => handleIssueBook(bookId),
      });
    } catch (error) {
      message.error("Failed to check book borrowing status. Please try again.");
    }
  };

  useEffect(() => {
    const checkDueDates = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "issuedBooks"));
        const currentDate = new Date();
        const booksToNotify = [];

        querySnapshot.forEach((docSnapshot) => {
          const issueDetails = docSnapshot.data();
          const dueDate = issueDetails.dueDate.toDate();
          const timeDiff = dueDate - currentDate;

          if (issueDetails.notified) {
            return;
          }

          const diffInDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

          const isThreeDaysBefore = diffInDays <= 3 && diffInDays >= 0;
          const isOnDueDate = diffInDays === 0;

          if (isThreeDaysBefore || isOnDueDate) {
            booksToNotify.push({ ...issueDetails, id: docSnapshot.id });
          }
        });
        for (const book of booksToNotify) {
          await sendEmailBorrower(book);

          const bookRef = doc(db, "issuedBooks", book.id);
          await updateDoc(bookRef, { notified: true });
        }
      } catch (error) {}
    };

    const intervalId = setInterval(checkDueDates, 86400000);
    checkDueDates();

    return () => clearInterval(intervalId);
  }, []);

  const fetchStaffData = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "staff"));
      const staffData = querySnapshot.docs
        .map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }))
        .filter((staff) => staff.designation === "Staff");

      setStaffList(staffData);
      setFilteredStaffList(staffData);
      setTotalStaffCount(staffData.length);

      setYearLevels([
        ...new Set(staffData.map((staff) => staff.yearLevel).filter(Boolean)),
      ]);
      setStatuses([
        ...new Set(staffData.map((staff) => staff.status).filter(Boolean)),
      ]);
      setGenders([
        ...new Set(staffData.map((staff) => staff.gender).filter(Boolean)),
      ]);
    } catch (error) {
      message.error("Error fetching staff data: " + error.message);
    }
    setLoading(false);
  };

  const checkUserIssues = async () => {
    try {
      const snapshot = await getDocs(collection(db, "issuedBooks"));
      const currentDate = new Date();
      const issuesMap = {};

      snapshot.docs.forEach((doc) => {
        const data = doc.data();

        // Skip resolved or paid
        if (["Resolved", "Paid"].includes(data.status)) return;

        const userId = data.staffId;
        if (!userId) return;

        if (!issuesMap[userId]) {
          issuesMap[userId] = new Set();
        }

        const isDamaged = data.status === "Damaged";
        const isMissing = data.status === "Missing";
        const isOverdue =
          data.status === "Good" && data.dueDate?.toDate() < currentDate;

        if (isDamaged) issuesMap[userId].add("Damaged");
        if (isMissing) issuesMap[userId].add("Missing");
        if (isOverdue) issuesMap[userId].add("Overdue");
      });

      const formattedMap = {};
      for (const userId in issuesMap) {
        formattedMap[userId] = Array.from(issuesMap[userId]);
      }

      setUserIssueStatus(formattedMap);
    } catch (err) {
      console.error("Failed to check user issues:", err);
    }
  };

  useEffect(() => {
    fetchStaffData();
    const unsubscribeBooks = listenToBooksData();
    checkUserIssues();

    return () => unsubscribeBooks();
  }, []);

  const logUserActivity = async (actionType, details) => {
    const userName = localStorage.getItem("userName");
    const logEntry = {
      actionType,
      performedBy: userName,
      details,
      timestamp: Timestamp.fromDate(new Date()),
    };

    try {
      await addDoc(collection(db, "user-activity-logs"), logEntry);
    } catch (error) {}
  };

  const handleAddStaff = async (values) => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "staff"));
      const existingStaff = querySnapshot.docs.find(
        (doc) =>
          doc.data().email === values.email ||
          doc.data().staffId === values.staffId
      );

      if (existingStaff) {
        message.error("Staff with this Email or Staff ID already exists!");
        setLoading(false);
        return;
      }

      await createUserWithEmailAndPassword(
        auth,
        values.email,
        "defaultPassword123"
      );

      // await emailjs.send(
      //   "service_lgmm1pe",
      //   "template_ow9j3wm",
      //   {
      //     name: values.name,
      //     email: values.email,
      //     password: "defaultPassword123",
      //   },
      //   "VLTWU3-FLPnBi2RaM"
      // );

      const libraryCardNumber = `LIB-${uuidv4().slice(0, 8).toUpperCase()}`;
      const avatarSeed = generateAvatarSeed(values.name || values.email);

      const newStaff = {
        ...values,
        status: "Active",
        libraryCardNumber,
        avatarSeed,
      };

      if (values.yearLevel) {
        newStaff.yearLevel = Array.isArray(values.yearLevel)
          ? values.yearLevel
          : [values.yearLevel];
      }

      await addDoc(collection(db, "staff"), newStaff);
      await logUserActivity("addUser", `Added new staff: ${values.name}`);

      if (!yearLevels.includes(newStaff.yearLevel))
        setYearLevels([...yearLevels, newStaff.yearLevel]);
      if (!designations.includes(newStaff.designation))
        setDesignations([...designations, newStaff.designation]);
      if (!statuses.includes(newStaff.status))
        setStatuses([...statuses, newStaff.status]);
      if (!genders.includes(newStaff.gender))
        setGenders([...genders, newStaff.gender]);

      message.success("Staff / Librarian added successfully!");
      handleCancel();
      fetchStaffData();
    } catch (error) {
      message.error("Error adding staff: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStaff = async (values) => {
    setLoading(true);
    try {
      const staffRef = doc(db, "staff", editingStaff.key);
      const cleanedValues = {
        ...values,
        yearLevel: Array.isArray(values.yearLevel)
          ? values.yearLevel
          : [values.yearLevel].filter(Boolean), // handles undefined/null
        designation:
          typeof values.designation === "string"
            ? values.designation.trim()
            : values.designation,
        status:
          typeof values.status === "string"
            ? values.status.trim()
            : values.status,
        gender:
          typeof values.gender === "string"
            ? values.gender.trim()
            : values.gender,
      };

      const changes = Object.keys(cleanedValues)
        .filter((key) => cleanedValues[key] !== editingStaff[key])
        .map((field) => ({
          field,
          oldValue: editingStaff[field] || "N/A",
          newValue: cleanedValues[field] || "N/A",
        }));

      if (changes.length === 0) {
        message.info("No changes detected.");
        setLoading(false);
        return;
      }

      const oldStatus = changes
        .map((change) => `${change.field}: ${change.oldValue}`)
        .join(", ");
      const newStatus = changes
        .map((change) => `${change.field}: ${change.newValue}`)
        .join(", ");
      const actionType = "editUser";

      await updateDoc(staffRef, cleanedValues);

      await logUserActivity(
        actionType,
        ` ${editingStaff.name} Status changed from ${oldStatus} to ${newStatus}`
      );

      if (editingStaff.email !== values.email) {
        const issuedBooksSnapshot = await getDocs(
          collection(db, "issuedBooks")
        );
        issuedBooksSnapshot.docs.forEach(async (docSnapshot) => {
          const data = docSnapshot.data();
          if (data.staffId === editingStaff.key) {
            const issuedBookRef = doc(db, "issuedBooks", docSnapshot.id);
            await updateDoc(issuedBookRef, { email: values.email });
          }
        });
      }

      message.success("Faculty updated successfully!");
      handleCancel();
      fetchStaffData();
    } catch (error) {
      message.error("Error editing staff: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (key) => {
    try {
      const staffToDelete = staffList.find((staff) => staff.key === key);
      await deleteDoc(doc(db, "staff", key));
      await logUserActivity(
        "deleteUser",
        `Deleted staff: ${staffToDelete.name}`
      );
      message.success("Faculty deleted successfully!");
      fetchStaffData();
    } catch (error) {
      message.error("Error deleting staff: " + error.message);
    }
  };

  const handleViewDetails = async (record) => {
    setLoading(true);
    try {
      const issuedBooksSnapshot = await getDocs(collection(db, "issuedBooks"));
      const userBorrowedBooks = issuedBooksSnapshot.docs
        .filter((docSnapshot) => docSnapshot.data().staffId === record.staffId)
        .map((docSnapshot) => ({
          id: docSnapshot.id,
          title: docSnapshot.data().title,
          author: docSnapshot.data().author,
          copiesBorrowed: docSnapshot.data().copiesBorrowed,
          dueDate: docSnapshot.data().dueDate,
          borrowedDate: docSnapshot.data().borrowedDate,
        }));

      setViewStaff({ ...record, borrowedBooks: userBorrowedBooks });
      setIsModalVisible(true);
    } catch (error) {
      message.error("Failed to fetch user borrowed books.");
    }
    setLoading(false);
  };

  const handleEditClick = (record) => {
    setEditingStaff(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const listenToBooksData = () => {
    const booksRef = collection(db, "books");
    const unsubscribe = onSnapshot(
      booksRef,
      (snapshot) => {
        const booksData = snapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        setBooksList(booksData);
      },
      (error) => {
        message.error("Error listening to books data: " + error.message);
      }
    );

    return unsubscribe; // for cleanup
  };

  const calculateDueDate = () => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() + 6);
    return currentDate.toISOString();
  };

  const handleIssueBook = async (bookId) => {
    if (!selectedStaff || !selectedBook) {
      message.error("Please select a Faculty member and a book first!");
      return;
    }

    try {
      const bookRef = doc(db, "books", bookId);

      await runTransaction(db, async (transaction) => {
        const bookDoc = await transaction.get(bookRef);

        if (!bookDoc.exists()) {
          throw new Error("The selected book does not exist.");
        }

        const bookData = bookDoc.data();

        if (parseInt(bookData.copiesAvailable) < numCopies) {
          throw new Error("Not enough copies available.");
        }

        const issuedBooksSnapshot = await getDocs(
          collection(db, "issuedBooks")
        );

        const alreadyIssued = issuedBooksSnapshot.docs.some((doc) => {
          const data = doc.data();
          return (
            data.bookId === bookId &&
            data.staffId === selectedStaff.key &&
            data.status !== "Resolved" &&
            data.status !== "Paid"
          );
        });

        if (alreadyIssued) {
          throw new Error(
            `${selectedStaff.name} has already borrowed "${bookData.title}" and has not returned it yet.`
          );
        }

        const issueDetails = {
          staffId: selectedStaff.staffId,
          libraryCardNumber: selectedStaff.libraryCardNumber,
          bookId: bookId,
          title: selectedBook.title,
          department: selectedBook.department,
          author: selectedBook.author,
          category: selectedBook.category,
          borrowedBy: selectedStaff.name,
          number: selectedStaff.number,
          email: selectedStaff.email,
          yearLevel: selectedStaff.yearLevel,
          borrowedDate: Timestamp.fromDate(new Date()),
          dueDate: Timestamp.fromDate(new Date(calculateDueDate())),
          duration: "6 months",
          issuedAt: Timestamp.fromDate(new Date()),
          accommodatedBy: localStorage.getItem("userName"),
          status: "Good",
          notified: false,
          copiesBorrowed: numCopies,
        };

        await addDoc(collection(db, "issuedBooks"), issueDetails);

        await logUserActivity(
          "issueBook",
          `Issued ${numCopies} copies of "${selectedBook?.title}" to ${selectedStaff?.name}`
        );

        const updatedCopiesAvailable =
          parseInt(bookData.copiesAvailable) - numCopies;
        transaction.update(bookRef, {
          copiesAvailable: updatedCopiesAvailable.toString(),
        });

        sendEmail(issueDetails);
      });

      message.success("Book issued successfully!");
      handleIssueBookModalCancel();
    } catch (error) {
      message.error(
        error.message || "Failed to issue the book. Please try again."
      );
    }
  };

  useEffect(() => {
    fetchStaffData();
    const unsubscribeBooks = listenToBooksData();

    return () => unsubscribeBooks(); // cleanup listener on unmount
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
    const filtered = staffList.filter(
      (staff) =>
        staff.name.toLowerCase().includes(value.toLowerCase()) ||
        staff.libraryCardNumber.toLowerCase().includes(value.toLowerCase()) ||
        staff.staffId.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredStaffList(filtered);
  };

  const columns = [
    {
      title: "Avatar",
      key: "avatar",
      render: (_, record) => (
        <Avatar
          className="custom-avatar"
          src={`https://api.dicebear.com/7.x/miniavs/svg?seed=${record.avatarSeed}`}
        />
      ),
    },
    {
      title: "Library Card Number",
      dataIndex: "libraryCardNumber",
      key: "libraryCardNumber",
    },
    {
      title: "Full Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Borrowed/Returned Status",
      key: "status",
      align: "center",
      render: (_, record) => {
        const issues = userIssueStatus[record.staffId];

        if (issues && issues.length > 0) {
          return (
            <Tooltip title={issues.join(", ")}>
              <Tag color="red">Has Issue</Tag>
            </Tooltip>
          );
        }

        return <Tag color="green">Good Standing</Tag>;
      },
    },

    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip
            title="View Faculty Details"
            overlayInnerStyle={{ backgroundColor: "#FFFFFF", color: "#000000" }}
            color="#FFFFFF"
          >
            <Button
              type="link"
              icon={<EyeOutlined className="action-icon" />}
              onClick={() => handleViewDetails(record)}
            ></Button>
          </Tooltip>
          <Tooltip
            title="Edit Faculty Details"
            overlayInnerStyle={{ backgroundColor: "#FFFFFF", color: "#000000" }}
            color="#FFFFFF"
          >
            <Button
              type="link"
              icon={<EditOutlined className="action-icon" />}
              onClick={() => handleEditClick(record)}
            ></Button>
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this staff?"
            onConfirm={() => handleDeleteStaff(record.key)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            {" "}
            <Tooltip
              title="Delete Faculty "
              overlayInnerStyle={{
                backgroundColor: "#FFFFFF",
                color: "#000000",
              }}
              color="#FFFFFF"
            >
              <Button
                type="link"
                icon={<DeleteOutlined className="action-icon" />}
              ></Button>
            </Tooltip>
          </Popconfirm>

          <Button
            type="primary"
            onClick={() => {
              setSelectedStaff(record);
              setIssueBookModalVisible(true);
            }}
          >
            Issue Book
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      className={`manage-user-container ${
        isModalVisible || issueBookModalVisible || borrowedBooksModalVisible
          ? "blurred"
          : ""
      }`}
    >
      {(isModalVisible ||
        issueBookModalVisible ||
        borrowedBooksModalVisible) && <div className="overlay" />}

      <div className="statistic-container statistic-student">
        <Statistic title=" 👨‍🏫 Total Faculty" value={totalStaffCount} />
      </div>
      <div className="search-main-container">
        <div className="add-btn">
          <button type="button" className="button" onClick={showModal}>
            <PlusCircleOutlined className="add-icon" />
            <span className="button__text">Add Librarian / Faculty</span>
          </button>
        </div>

        <div className="search-container">
          <Search
            className="custom-search"
            placeholder="Search faculty name or card number here..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            enterButton="Search"
            size="large"
          />
        </div>
      </div>
      <div className="table-container">
        <Table
          className="custom-table"
          columns={columns}
          dataSource={filteredStaffList}
          pagination={{ pageSize: 4, size: "middle" }}
          locale={{
            emptyText: loading ? (
              <Spin tip="Loading Faculty..." />
            ) : (
              "No Faculty or Librarian Found. Try searching for a different Name!"
            ),
          }}
          size="middle"
        />
      </div>
      <Modal
        className="custom-modal"
        title="Faculty / Librarian Details"
        visible={isModalVisible && !editingStaff}
        onCancel={handleCancel}
        footer={null}
      >
        {viewStaff && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="First Name">
              {viewStaff.name}
            </Descriptions.Item>
            <Descriptions.Item label="Middle Name">
              {viewStaff.middleName}
            </Descriptions.Item>
            <Descriptions.Item label="Last Name">
              {viewStaff.lastName}
            </Descriptions.Item>
            <Descriptions.Item label="Suffix Name">
              {viewStaff.suffix}
            </Descriptions.Item>
            <Descriptions.Item label="Library Card Number">
              {viewStaff.libraryCardNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {" "}
              {viewStaff.email}
            </Descriptions.Item>
            <Descriptions.Item label="Phone Number">
              {viewStaff.number}
            </Descriptions.Item>
            <Descriptions.Item label="Faculty ID Number">
              {viewStaff.staffId}
            </Descriptions.Item>
            <Descriptions.Item label="Year Level">
              {viewStaff.yearLevel}
            </Descriptions.Item>

            <Descriptions.Item label="Designation">
              {viewStaff.designation}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {" "}
              {viewStaff.status}
            </Descriptions.Item>
            <Descriptions.Item label="Issue Status">
              {userIssueStatus[viewStaff?.staffId]?.length > 0 ? (
                <>
                  <Tag color="red">Has Issue</Tag>
                  <span>
                    {" "}
                    ({userIssueStatus[viewStaff.staffId].join(", ")})
                  </span>
                </>
              ) : (
                <Tag color="green">Good Standing</Tag>
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Gender">
              {" "}
              {viewStaff.gender}
            </Descriptions.Item>
            <Descriptions.Item label="Books Borrowed">
              {viewStaff.borrowedBooks && viewStaff.borrowedBooks.length > 0 ? (
                <Button
                  type="link"
                  style={{
                    color: "#1890ff",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                  onMouseOver={(e) =>
                    (e.target.style.textDecoration = "underline")
                  }
                  onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                  onClick={() => {
                    setBorrowedBooks(viewStaff.borrowedBooks);
                    setBorrowedBooksModalVisible(true);
                  }}
                >
                  {viewStaff.borrowedBooks.reduce(
                    (total, book) => total + (book.copiesBorrowed || 0),
                    0
                  )}
                </Button>
              ) : (
                <span style={{ color: "#999" }}>None</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        className="custom-modal"
        title="Borrowed Books"
        visible={borrowedBooksModalVisible}
        onCancel={() => setBorrowedBooksModalVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setBorrowedBooksModalVisible(false)}
          >
            Close
          </Button>,
        ]}
      >
        {borrowedBooks.length > 0 ? (
          <Table
            dataSource={borrowedBooks}
            columns={[
              {
                title: "Book Title",
                dataIndex: "title",
                key: "title",
              },
              {
                title: "Author",
                dataIndex: "author",
                key: "author",
              },
              {
                title: "Number of Book Copies",
                dataIndex: "copiesBorrowed",
                key: "copiesBorrowed",
              },
              {
                title: "Borrowed Date",
                dataIndex: "borrowedDate",
                key: "borrowedDate",
                render: (borrowedDate) =>
                  borrowedDate
                    ? format(borrowedDate.toDate(), "MMMM dd, yyyy, hh:mm a")
                    : "N/A",
              },
              {
                title: "Due Date",
                dataIndex: "dueDate",
                key: "dueDate",
                render: (dueDate) =>
                  dueDate
                    ? format(dueDate.toDate(), "MMMM dd, yyyy, hh:mm a")
                    : "N/A",
              },
            ]}
            pagination={false}
            rowKey="id"
          />
        ) : (
          <p>No books borrowed</p>
        )}
      </Modal>

      <Modal
        className="custom-modal"
        title={
          editingStaff ? "Edit Faculty Details" : "Add Librarian / Faculty"
        }
        visible={isModalVisible && !viewStaff}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingStaff ? handleEditStaff : handleAddStaff}
          onValuesChange={(changedValues) => {
            if (changedValues.designation) {
              const desig = Array.isArray(changedValues.designation)
                ? changedValues.designation[0]
                : changedValues.designation;
              setSelectedDesignation(desig);
            }
          }}
          initialValues={editingStaff || {}}
        >
          <Form.Item
            label="Choose Designation You Want To Add"
            name="designation"
            rules={[
              { required: true, message: "Please select a Designation!" },
            ]}
          >
            <Select
              className="dept-option"
              placeholder="Select a Designation"
              onChange={(value) => {
                setSelectedDesignation(value);
                form.setFieldsValue({ designation: value });
              }}
              value={selectedDesignation}
            >
              <Select.Option value="Staff">Faculty</Select.Option>
              <Select.Option value="Librarian">Librarian</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="First Name"
            name="name"
            rules={[
              { required: true, message: "Please input the staff's name!" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Middle Name"
            name="middleName"
            rules={[
              {
                required: true,
                message: "Please input the staff's Middle Name!",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: "Please enter the Last Name!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Suffix (Optional)" name="suffix">
            <Input />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please input the staff's email!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="number"
            rules={[
              {
                required: true,
                message: "Please input the staff's phone number!",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Faculty / Librarian   ID Number - Ex: AY2021-12344"
            name="staffId"
            rules={[
              { required: true, message: "Please input the staff's ID!" },
            ]}
          >
            <Input />
          </Form.Item>
          {selectedDesignation !== "Librarian" && (
            <Form.Item
              label="Year Level"
              name="yearLevel"
              rules={[
                { required: true, message: "Please enter a Year Level!" },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Select or Input Year Levels"
                className="dept-option"
              >
                {yearLevels.map((level) => (
                  <Select.Option key={level} value={level}>
                    {level}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {selectedDesignation !== "Librarian" && (
            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select a Status!" }]}
            >
              <Select
                placeholder="Select Status"
                className="dept-option"
                allowClear // Optional: allows user to clear selection
              >
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Gender"
            name="gender"
            rules={[{ required: true, message: "Please enter a Gender!" }]}
          >
            <Select
              mode="tags"
              placeholder="Select or Input a Gender"
              className="dept-option"
              value={
                form.getFieldValue("gender")
                  ? [form.getFieldValue("gender")]
                  : []
              }
              onChange={(value) => {
                if (value.length > 1) value = [value[value.length - 1]];
                form.setFieldsValue({ gender: value[0] || undefined });
              }}
              onInputKeyDown={(e) => {
                if (e.key === "Backspace" && !form.getFieldValue("gender")) {
                  form.setFieldsValue({ gender: undefined });
                }
              }}
            >
              {genders.map((gender) => (
                <Select.Option key={gender} value={gender}>
                  {gender}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div className="modal-footer">
            <Button
              onClick={handleCancel}
              className="cancel-btn"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="save-btn"
              loading={loading}
            >
              {loading ? "Saving..." : editingStaff ? "Save Changes" : "Save"}
            </Button>
          </div>
        </Form>
      </Modal>
      <Modal
        className="custom-modal"
        title="Issue Book"
        visible={issueBookModalVisible}
        onCancel={handleIssueBookModalCancel}
        footer={null}
      >
        {selectedStaff && (
          <div>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <Avatar
                size={64}
                src={`https://api.dicebear.com/7.x/miniavs/svg?seed=${selectedStaff.avatarSeed}`}
              />
            </div>

            <Descriptions bordered column={1}>
              <Descriptions.Item label="First Name">
                {selectedStaff.name}
              </Descriptions.Item>
              <Descriptions.Item label="Library Card Number">
                {selectedStaff.libraryCardNumber}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        <Form>
          <Form.Item label="Select Book">
            <Select
              className="dept-option"
              showSearch
              placeholder="Search and Select a Book to Issue"
              value={selectedBook?.key}
              onChange={(value) => {
                const book = booksList.find((b) => b.key === value);
                setSelectedBook(book);
              }}
              filterOption={(input, option) =>
                option?.label?.toLowerCase()?.includes(input.toLowerCase()) ||
                false
              }
              style={{
                width: "100%",
                fontSize: "16px",
                fontWeight: "bold",
                borderRadius: "6px",
              }}
              dropdownStyle={{
                width: "550px",
                maxHeight: "400px",
                overflow: "auto",
                background: "#f9f9f9",
                borderRadius: "8px",
                boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              }}
              optionLabelProp="label"
              allowClear
              dropdownRender={(menu) => (
                <div
                  style={{
                    width: "550px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    padding: "10px",
                  }}
                >
                  {menu}
                </div>
              )}
            >
              {booksList.map((book) => (
                <Select.Option
                  key={book.key}
                  value={book.key}
                  label={book.title || "Unknown Title"}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "10px",
                      borderRadius: "6px",
                      transition: "background 0.3s ease-in-out",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        📖 {book.title || "No Title"}
                      </span>
                      <div style={{ fontSize: "12px", color: "#555" }}>
                        ✍️ Author: {book.author || "Unknown"}
                      </div>
                      <div style={{ fontSize: "12px", color: "#777" }}>
                        📚 Category: {book.category || "N/A"}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color:
                            book.copiesAvailable > 0 ? "#27ae60" : "#c0392b",
                        }}
                      >
                        🏷️ Available: {book.copiesAvailable || "0"}
                      </div>
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Number of Copies"
            rules={[
              {
                required: true,
                message: "Please enter a valid number of copies.",
              },
            ]}
          >
            <Input
              type="number"
              min={1}
              value={numCopies}
              onChange={(e) => setNumCopies(Number(e.target.value))}
            />
          </Form.Item>

          <Button
            type="primary"
            style={{ width: "100%" }}
            onSelect={(value) => handleBookSelection(value)}
            onClick={() => handleBookSelection(selectedBook?.key)}
          >
            Issue Book
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageUser;
